import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { DocumentService } from 'src/app/core/service/document.service';
import { WebcamImage, WebcamInitError, WebcamUtil } from 'ngx-webcam';
import { Observable, Subject } from 'rxjs';
import { ProgressColor } from '@progress/kendo-angular-progressbar';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AnimationEndEvent } from '@progress/kendo-angular-progressbar/types/animation-end-event';
import { TranslateService } from '@ngx-translate/core';
import { TokenCaptureLink } from '../model/token-capture-link';

@Component({
  standalone: false,
  selector: 'app-document-link-upload',
  templateUrl: './document-link-upload.component.html',
  // styleUrls: ['./document-link-upload.component.scss']
  encapsulation: ViewEncapsulation.None,
  styles: [`
  .webcam-wrapper video { max-width: 100%; height: auto;}
  .snapshot { width: 100px; }
  .delete-image-icon {
    position: relative;
    top: 50%;
    left: -4px;
  }
  .upload-visibility { visibility: hidden; }
  .completed-container {
    margin: auto;
    font-size: 14pt;
    border: solid 1px;
    padding: 10px;
    background-color: #78924f;
    color: #fff;
    border-radius: 8px;
    width: 250px;
  }
  `]
})
export class DocumentLinkUploadComponent implements OnInit
{
  public model: TokenCaptureLink = new TokenCaptureLink();
  public memberName: string;
  public isValidToken = false;
  public validating = true;
  public showWebcam = true;
  public allowCameraSwitch = true;
  public multipleWebcamsAvailable = false;
  public deviceId: string;
  public uploading = false;
  public completed = false;
  public hasReportImage = false;
  public reportImageLoaded = false;
  public reportImageUrl: string;
  public errors: WebcamInitError[] = [];
  public webcamImage: WebcamImage = null; // Latest snapshot
  public images = [];
  public progressValue = 100;
  public tipsText = [];
  public videoOptions: MediaTrackConstraints =
    {
      width: { ideal: 1024 },
      height: { ideal: 800 }
    };

  public colors: ProgressColor[] = [
    {
      from: 0,
      to: 25,
      color: '#aabf88'
    },
    {
      from: 25,
      to: 50,
      color: '#9ab372'
    },
    {
      from: 50,
      to: 75,
      color: '#8aa75c'
    },
    {
      from: 75,
      to: 100,
      color: '#78924f'
    }
  ];

  private trigger: Subject<void> = new Subject<void>();
  private nextWebcam: Subject<boolean | string> = new Subject<boolean | string>();

  constructor(
    private route: ActivatedRoute,
    private messageHandlerService: MessageHandlerService,
    private translate: TranslateService,
    private documentService: DocumentService)
  {
    this.translate.get(['DOCUMENT-CAPTURE-TIP-LINE1', 'DOCUMENT-CAPTURE-TIP-LINE2',
      'DOCUMENT-CAPTURE-TIP-LINE3', 'DOCUMENT-CAPTURE-TIP-LINE4']).subscribe({
        next: (res) =>
        {
          this.tipsText.push(res['DOCUMENT-CAPTURE-TIP-LINE1']);
          this.tipsText.push(res['DOCUMENT-CAPTURE-TIP-LINE2']);
          this.tipsText.push(res['DOCUMENT-CAPTURE-TIP-LINE3']);
          this.tipsText.push(res['DOCUMENT-CAPTURE-TIP-LINE4']);
        }
      });
  }

  public get triggerObservable(): Observable<void>
  {
    return this.trigger.asObservable();
  }

  public get nextWebcamObservable(): Observable<boolean | string>
  {
    return this.nextWebcam.asObservable();
  }

  public ngOnInit(): void
  {
    this.getReportPhotoFromService();
    this.initializations();
    this.route.params.forEach((params: Params) =>
    {
      if (params.token !== undefined)
      {
        this.model.token = params.token;
        delete this.model.snapshot;
        delete this.model.mimeType;
        this.documentService.verifyImageCapture(this.model).subscribe({
          next: (data) =>
          {
            if (data !== null && data !== undefined)
            {
              this.memberName = data.memberName;
              this.isValidToken = true;

              WebcamUtil.getAvailableVideoInputs()
                .then((mediaDevices: MediaDeviceInfo[]) =>
                {
                  this.multipleWebcamsAvailable = mediaDevices && mediaDevices.length > 1;
                });
            }
            else
            {
              this.isValidToken = false;
            }
          },
          complete: () =>
          {
            this.validating = false;
          }
        });
      }
    });
  }

  public triggerSnapshot(): void
  {
    if (this.uploading) { return; }
    this.trigger.next();
  }

  public toggleWebcam(): void
  {
    if (this.uploading) { return; }
    this.errors = [];
    this.showWebcam = !this.showWebcam;
  }

  public handleInitError(error: WebcamInitError): void
  {
    this.errors.push(error);
  }

  public showNextWebcam(directionOrDeviceId: boolean | string): void
  {
    if (this.uploading) { return; }
    // true => move forward through devices
    // false => move backwards through devices
    // string => move to device with given deviceId
    this.nextWebcam.next(directionOrDeviceId);
  }

  public handleImage(webcamImage: WebcamImage): void
  {
    this.webcamImage = webcamImage;
    this.images.push(webcamImage);
  }

  public deleteImage(index: number): void
  {
    const newImages = [];
    for (let i = 0; i < this.images.length; i++)
    {
      if (i !== index)
      {
        newImages.push(this.images[i]);
      }
    }
    this.images = newImages;
  }

  public uploadAllImages(): void
  {
    if (this.uploading) { return; }
    this.progressValue = 0;
    let uploadCount = this.images.length;
    this.uploading = true;
    for (let index = 0; index < this.images.length; index++)
    {
      // Upload the image
      this.model.mimeType = this.getMimeType(this.images[index].imageAsDataUrl);
      this.model.snapshot = this.images[index].imageAsBase64;
      this.documentService.uploadImageCapture(this.model).subscribe({
        error: (errorMessage) =>
        {
          this.messageHandlerService.displayError(errorMessage);
          this.uploading = false;
        },
        complete: () =>
        {
          uploadCount -= 1;
          // Calculate progress
          this.progressValue = uploadCount === 0 ? 100 : Math.round(((index + 1) / this.images.length) * 100);
        }
      });
    }
  }

  public endUpload(event: AnimationEndEvent): void
  {
    if (this.progressValue === 100 && this.progressValue === event.to)
    {
      this.documentService.updateCaptureLink(this.model).subscribe({
        next: () =>
        {
          this.completed = true;
          this.uploading = false;
        }
      });
    }
  }

  /**
   * Return the mimeType of the snapshot taken.
   *
   * @param value File value
   * @returns Mime type
   */
  private getMimeType(value: string): string
  {
    let mimeType = '';
    const array = value.split(';');
    if (array.length === 2)
    {
      if (array[0].split(':').length === 2)
      {
        mimeType = array[0].split(':')[1];
      }
    }
    return mimeType;
  }

  private initializations(): void
  {
    this.images = [];
    this.memberName = null;
    this.validating = true;
    this.uploading = false;
    this.isValidToken = false;
    this.completed = false;
  }

  private getReportPhotoFromService()
  {
    this.reportImageLoaded = false;
    this.hasReportImage = false;
    this.documentService.getReportImage().subscribe({
      next: (data) =>
      {
        this.hasReportImage = data && data.content !== null;
        if (this.hasReportImage)
        {
          const base64 = data.content;
          this.reportImageUrl = 'data:image/jpeg;base64,' + base64;
        }
        this.reportImageLoaded = true;
      }
    });
  }
}
